import useCloseOnEscape from 'components/crm/ui/useCloseOnEscape';
import { useRef, useState } from 'react';

const sameValue = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/**
 * A field of the task panel changed in place: `start` opens its editor on the
 * saved value, `commit(next)` closes it and saves `next` through `onSave` when
 * it differs, Escape (`cancel`) closes it without a call. While the call runs,
 * `shown` is the new value; `onSave` resolves false on a refusal (the panel
 * already said why), and `shown` falls back to the saved value.
 */
const useInlineField = ({ value, onSave, same = sameValue }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraftState] = useState(value);
  const [pending, setPending] = useState(null);
  // A blur may follow Enter or Escape on the same editor: only the first ends it.
  const open = useRef(false);
  const latestDraft = useRef(value);
  const latestValue = useRef(value);
  latestValue.current = value;

  const setDraft = (next) => {
    latestDraft.current = next;
    setDraftState(next);
  };
  const start = () => {
    open.current = true;
    setDraft(latestValue.current);
    setEditing(true);
  };
  const cancel = () => {
    if (!open.current) return;
    open.current = false;
    setEditing(false);
  };
  const commit = async (next = latestDraft.current) => {
    if (!open.current) return;
    open.current = false;
    setEditing(false);
    if (same(next, latestValue.current)) return;
    setPending({ value: next });
    await onSave(next);
    setPending(null);
  };
  // Taken before the drawer's own Escape, which would close the whole panel.
  useCloseOnEscape(editing, cancel);

  return { editing, draft, setDraft, start, cancel, commit, shown: pending ? pending.value : value };
};

export default useInlineField;
